import { apiClient } from './client'

// ============ TYPE DEFINITIONS ============

export interface BookCategory {
  _id: string
  name: string
  description?: string
  institution: string
  parentCategory?: string | BookCategory
  colorCode: string
  icon: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface Book {
  _id: string
  title: string
  subtitle?: string
  authors: string[]
  isbn: string
  isbn13?: string
  barcode: string
  publisher?: string
  publishedDate?: string
  edition?: string
  language: string
  pageCount?: number
  description?: string
  coverImage?: string
  category: string | BookCategory
  subcategory?: string | BookCategory
  institution: string
  tags: string[]
  totalCopies: number
  availableCopies: number
  reservedCopies: number
  shelfLocation?: string
  deweyDecimal?: string
  acquisitionDate?: string
  acquisitionSource?: 'purchase' | 'donation' | 'transfer' | 'other'
  price?: number
  currency: string
  isReferenceOnly: boolean
  isActive: boolean
  createdBy: string
  createdAt: string
  updatedAt: string
  copies?: BookCopy[]
}

export type BookCopyCondition = 'excellent' | 'good' | 'fair' | 'poor' | 'damaged' | 'lost'
export type BookCopyStatus = 'available' | 'borrowed' | 'reserved' | 'maintenance' | 'lost' | 'retired'

export interface BookCopy {
  _id: string
  book: string | Book
  institution: string
  copyNumber: number
  barcode: string
  condition: BookCopyCondition
  status: BookCopyStatus
  notes?: string
  lastBorrowedDate?: string
  lastReturnedDate?: string
  totalTimesLoaned: number
  currentBorrowing?: string | Borrowing
  acquiredDate: string
  retiredDate?: string
  retiredReason?: string
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export type BorrowingStatus = 'active' | 'returned' | 'overdue' | 'lost' | 'renewed'

export interface Borrowing {
  _id: string
  bookCopy: string | BookCopy
  book: string | Book
  borrower: string | BorrowerInfo
  borrowerType: 'student' | 'teacher' | 'staff'
  institution: string
  borrowedDate: string
  dueDate: string
  returnedDate?: string
  status: BorrowingStatus
  renewalCount: number
  maxRenewals: number
  issuedBy: string | { firstName: string; lastName: string }
  returnedTo?: string
  notes?: string
  conditionAtBorrow: BookCopyCondition
  conditionAtReturn?: BookCopyCondition
  fines: string[] | Fine[]
  remindersSent: number
  lastReminderDate?: string
  createdAt: string
  updatedAt: string
  daysOverdue?: number
}

export interface BorrowerInfo {
  _id: string
  firstName: string
  lastName: string
  email: string
  studentId?: string
  phone?: string
  profileImage?: string
}

export type FineType = 'late_return' | 'lost_book' | 'damaged_book' | 'lost_card' | 'other'
export type FineStatus = 'pending' | 'partial' | 'paid' | 'waived' | 'written_off'

export interface FinePayment {
  amount: number
  paidDate: string
  paymentMethod: string
  receiptNumber?: string
  collectedBy: string | { firstName: string; lastName: string }
  notes?: string
}

export interface Fine {
  _id: string
  borrowing?: string | Borrowing
  user: string | BorrowerInfo
  institution: string
  fineType: FineType
  amount: number
  currency: string
  paidAmount: number
  status: FineStatus
  reason: string
  daysOverdue?: number
  ratePerDay?: number
  issuedDate: string
  dueDate: string
  paidDate?: string
  waivedDate?: string
  waivedBy?: string | { firstName: string; lastName: string }
  waivedReason?: string
  payments: FinePayment[]
  createdBy: string
  createdAt: string
  updatedAt: string
}

export interface BorrowingRule {
  userType: 'student' | 'teacher' | 'staff'
  maxBooks: number
  loanDurationDays: number
  maxRenewals: number
  renewalDurationDays: number
  canBorrowReferenceBooks: boolean
  canReserveBooks: boolean
  maxReservations: number
}

export interface FineRules {
  lateReturnFinePerDay: number
  maxLateFine: number
  lostBookFineMultiplier: number
  damagedBookFinePercentage: number
  gracePeriodDays: number
  currency: string
}

export interface OperatingHours {
  dayOfWeek: number
  isOpen: boolean
  openTime?: string
  closeTime?: string
}

export interface ReservationRules {
  maxDaysInAdvance: number
  holdDurationDays: number
  autoExpireReservations: boolean
}

export interface NotificationSettings {
  sendDueReminders: boolean
  reminderDaysBefore: number[]
  sendOverdueNotices: boolean
  overdueNoticeDays: number[]
  sendFineNotices: boolean
  notificationChannels: ('email' | 'sms' | 'push')[]
}

export interface LibraryPolicy {
  _id: string
  institution: string
  name: string
  description?: string
  borrowingRules: BorrowingRule[]
  fineRules: FineRules
  operatingHours: OperatingHours[]
  reservationRules: ReservationRules
  notifications: NotificationSettings
  isDefault: boolean
  isActive: boolean
  createdBy: string
  createdAt: string
  updatedAt: string
}

export type ReservationStatus = 'pending' | 'ready' | 'fulfilled' | 'cancelled' | 'expired'

export interface BookReservation {
  _id: string
  book: string | Book
  user: string | BorrowerInfo
  institution: string
  reservationDate: string
  expiryDate: string
  status: ReservationStatus
  queuePosition: number
  notifiedDate?: string
  fulfilledDate?: string
  cancelledDate?: string
  cancelledReason?: string
  borrowing?: string
  notes?: string
  createdAt: string
  updatedAt: string
}

export interface LibraryStats {
  overview: {
    totalBooks: number
    totalCopies: number
    availableCopies: number
    borrowedCopies: number
    activeBorrowings: number
    overdueBorrowings: number
    totalCategories: number
    pendingFines: {
      amount: number
      count: number
    }
  }
  recentBorrowings: Borrowing[]
  popularBooks: {
    _id: string
    count: number
    book: {
      title: string
      authors: string[]
      coverImage?: string
    }
  }[]
  borrowingTrends: {
    _id: string
    borrowings: number
  }[]
  returnTrends: {
    _id: string
    returns: number
  }[]
  categoryBreakdown: {
    _id: string
    name: string
    colorCode: string
    bookCount: number
    totalCopies: number
    availableCopies: number
  }[]
}

export interface DashboardMetrics {
  todayBorrowings: number
  todayReturns: number
  dueTodayCount: number
  newBooksThisMonth: number
  finesCollectedToday: number
}

// ============ REQUEST TYPES ============

export interface CreateBookRequest {
  title: string
  subtitle?: string
  authors: string[]
  isbn: string
  isbn13?: string
  barcode: string
  publisher?: string
  publishedDate?: string
  edition?: string
  language?: string
  pageCount?: number
  description?: string
  coverImage?: string
  category: string
  subcategory?: string
  tags?: string[]
  totalCopies?: number
  shelfLocation?: string
  deweyDecimal?: string
  acquisitionDate?: string
  acquisitionSource?: 'purchase' | 'donation' | 'transfer' | 'other'
  price?: number
  currency?: string
  isReferenceOnly?: boolean
}

export interface UpdateBookRequest extends Partial<CreateBookRequest> {}

export interface GetBooksParams {
  page?: number
  limit?: number
  search?: string
  category?: string
  status?: string
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
  isReferenceOnly?: boolean
  hasAvailableCopies?: boolean
}

export interface BorrowBookRequest {
  bookCopyId: string
  borrowerId: string
  borrowerType: 'student' | 'teacher' | 'staff'
  dueDate?: string
  notes?: string
}

export interface BorrowBookByBarcodeRequest {
  barcode: string
  borrowerId: string
  borrowerType: 'student' | 'teacher' | 'staff'
  dueDate?: string
  notes?: string
}

export interface ReturnBookRequest {
  conditionAtReturn?: BookCopyCondition
  notes?: string
  createFineIfDamaged?: boolean
}

export interface ReturnBookByBarcodeRequest extends ReturnBookRequest {
  barcode: string
}

export interface GetBorrowingsParams {
  page?: number
  limit?: number
  status?: BorrowingStatus
  borrowerId?: string
  bookId?: string
  overdue?: boolean
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

export interface GetFinesParams {
  page?: number
  limit?: number
  status?: FineStatus
  userId?: string
  fineType?: FineType
  sortBy?: string
  sortOrder?: 'asc' | 'desc'
}

export interface CreateFineRequest {
  userId: string
  fineType: FineType
  amount: number
  reason: string
  dueDate?: string
  borrowingId?: string
}

export interface PayFineRequest {
  amount: number
  paymentMethod: string
  receiptNumber?: string
  notes?: string
}

export interface CreateCategoryRequest {
  name: string
  description?: string
  parentCategory?: string
  colorCode?: string
  icon?: string
}

export interface CreateReservationRequest {
  bookId: string
  userId: string
  notes?: string
}

export interface CreatePolicyRequest {
  name: string
  description?: string
  borrowingRules?: BorrowingRule[]
  fineRules?: FineRules
  operatingHours?: OperatingHours[]
  reservationRules?: ReservationRules
  notifications?: NotificationSettings
  isDefault?: boolean
}

// ============ RESPONSE TYPES ============

export interface PaginatedResponse<T> {
  success: boolean
  data: T[]
  pagination: {
    page: number
    limit: number
    total: number
    pages: number
  }
}

export interface ApiResponse<T> {
  success: boolean
  message?: string
  data: T
}

// ============ API FUNCTIONS ============

// Categories
export const getCategories = async (params?: { includeInactive?: boolean; parentOnly?: boolean }) => {
  const searchParams = new URLSearchParams()
  if (params?.includeInactive) searchParams.append('includeInactive', 'true')
  if (params?.parentOnly) searchParams.append('parentOnly', 'true')
  const response = await apiClient.get(`/library/categories?${searchParams.toString()}`)
  return response.data
}

export const createCategory = async (data: CreateCategoryRequest) => {
  const response = await apiClient.post('/library/categories', data)
  return response.data
}

export const updateCategory = async (id: string, data: Partial<CreateCategoryRequest>) => {
  const response = await apiClient.put(`/library/categories/${id}`, data)
  return response.data
}

export const deleteCategory = async (id: string) => {
  const response = await apiClient.delete(`/library/categories/${id}`)
  return response.data
}

// Books
export const getBooks = async (params: GetBooksParams = {}) => {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value.toString())
    }
  })
  const response = await apiClient.get(`/library/books?${searchParams.toString()}`)
  return response.data
}

export const getBook = async (id: string) => {
  const response = await apiClient.get(`/library/books/${id}`)
  return response.data
}

export const getBookByBarcode = async (barcode: string) => {
  const response = await apiClient.get(`/library/books/barcode/${barcode}`)
  return response.data
}

export const createBook = async (data: CreateBookRequest) => {
  const response = await apiClient.post('/library/books', data)
  return response.data
}

export const updateBook = async (id: string, data: UpdateBookRequest) => {
  const response = await apiClient.put(`/library/books/${id}`, data)
  return response.data
}

export const deleteBook = async (id: string) => {
  const response = await apiClient.delete(`/library/books/${id}`)
  return response.data
}

// Book Copies
export const addBookCopy = async (bookId: string, data: { condition?: BookCopyCondition; notes?: string; barcode?: string }) => {
  const response = await apiClient.post(`/library/books/${bookId}/copies`, data)
  return response.data
}

export const updateBookCopy = async (id: string, data: { condition?: BookCopyCondition; status?: BookCopyStatus; notes?: string }) => {
  const response = await apiClient.put(`/library/copies/${id}`, data)
  return response.data
}

export const retireBookCopy = async (id: string, reason: string) => {
  const response = await apiClient.post(`/library/copies/${id}/retire`, { reason })
  return response.data
}

// Borrowings
export const getBorrowings = async (params: GetBorrowingsParams = {}) => {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value.toString())
    }
  })
  const response = await apiClient.get(`/library/borrowings?${searchParams.toString()}`)
  return response.data
}

export const getUserBorrowingHistory = async (userId: string, params?: { page?: number; limit?: number; status?: BorrowingStatus }) => {
  const searchParams = new URLSearchParams()
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) searchParams.append(key, value.toString())
    })
  }
  const response = await apiClient.get(`/library/borrowings/user/${userId}?${searchParams.toString()}`)
  return response.data
}

export const getOverdueBooks = async (params?: { page?: number; limit?: number }) => {
  const searchParams = new URLSearchParams()
  if (params?.page) searchParams.append('page', params.page.toString())
  if (params?.limit) searchParams.append('limit', params.limit.toString())
  const response = await apiClient.get(`/library/borrowings/overdue?${searchParams.toString()}`)
  return response.data
}

export const borrowBook = async (data: BorrowBookRequest) => {
  const response = await apiClient.post('/library/borrowings', data)
  return response.data
}

export const borrowBookByBarcode = async (data: BorrowBookByBarcodeRequest) => {
  const response = await apiClient.post('/library/borrowings/barcode', data)
  return response.data
}

export const returnBook = async (borrowingId: string, data?: ReturnBookRequest) => {
  const response = await apiClient.post(`/library/borrowings/${borrowingId}/return`, data || {})
  return response.data
}

export const returnBookByBarcode = async (data: ReturnBookByBarcodeRequest) => {
  const response = await apiClient.post('/library/borrowings/return/barcode', data)
  return response.data
}

export const renewBorrowing = async (borrowingId: string) => {
  const response = await apiClient.post(`/library/borrowings/${borrowingId}/renew`)
  return response.data
}

export const markBookAsLost = async (borrowingId: string) => {
  const response = await apiClient.post(`/library/borrowings/${borrowingId}/lost`)
  return response.data
}

export const updateOverdueStatus = async () => {
  const response = await apiClient.post('/library/borrowings/update-overdue')
  return response.data
}

// Fines
export const getFines = async (params: GetFinesParams = {}) => {
  const searchParams = new URLSearchParams()
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      searchParams.append(key, value.toString())
    }
  })
  const response = await apiClient.get(`/library/fines?${searchParams.toString()}`)
  return response.data
}

export const getUserFines = async (userId: string, status?: FineStatus) => {
  const searchParams = new URLSearchParams()
  if (status) searchParams.append('status', status)
  const response = await apiClient.get(`/library/fines/user/${userId}?${searchParams.toString()}`)
  return response.data
}

export const createFine = async (data: CreateFineRequest) => {
  const response = await apiClient.post('/library/fines', data)
  return response.data
}

export const payFine = async (fineId: string, data: PayFineRequest) => {
  const response = await apiClient.post(`/library/fines/${fineId}/pay`, data)
  return response.data
}

export const waiveFine = async (fineId: string, reason: string) => {
  const response = await apiClient.post(`/library/fines/${fineId}/waive`, { reason })
  return response.data
}

// Policies
export const getPolicies = async (activeOnly?: boolean) => {
  const params = activeOnly ? '?activeOnly=true' : ''
  const response = await apiClient.get(`/library/policies${params}`)
  return response.data
}

export const createPolicy = async (data: CreatePolicyRequest) => {
  const response = await apiClient.post('/library/policies', data)
  return response.data
}

export const updatePolicy = async (id: string, data: Partial<CreatePolicyRequest>) => {
  const response = await apiClient.put(`/library/policies/${id}`, data)
  return response.data
}

export const deletePolicy = async (id: string) => {
  const response = await apiClient.delete(`/library/policies/${id}`)
  return response.data
}

// Reservations
export const getReservations = async (params?: { status?: ReservationStatus; bookId?: string; userId?: string; page?: number; limit?: number }) => {
  const searchParams = new URLSearchParams()
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined) searchParams.append(key, value.toString())
    })
  }
  const response = await apiClient.get(`/library/reservations?${searchParams.toString()}`)
  return response.data
}

export const createReservation = async (data: CreateReservationRequest) => {
  const response = await apiClient.post('/library/reservations', data)
  return response.data
}

export const cancelReservation = async (id: string, reason?: string) => {
  const response = await apiClient.post(`/library/reservations/${id}/cancel`, { reason })
  return response.data
}

// Statistics
export const getLibraryStats = async (startDate?: string, endDate?: string) => {
  const searchParams = new URLSearchParams()
  if (startDate) searchParams.append('startDate', startDate)
  if (endDate) searchParams.append('endDate', endDate)
  const response = await apiClient.get(`/library/stats?${searchParams.toString()}`)
  return response.data
}

export const getDashboardMetrics = async () => {
  const response = await apiClient.get('/library/dashboard')
  return response.data
}

// Get user by RFID card number (for library borrowings)
export const getUserByRFIDCard = async (cardNumber: string) => {
  const response = await apiClient.get(`/rfid/cards/user/${encodeURIComponent(cardNumber)}`)
  return response.data
}
